import { Form, Formik } from "formik";
import { PropsWithChildren, ReactNode, useRef } from "react";

import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import { Badge, Checkbox, FormControl, ListItemText, MenuItem, OutlinedInput, Select } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import { SxProps, Theme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import ExportDropDown from "@components/CompanySelect/ExportDropDown";
import ResourceLink from "@components/ResourceLink";
import tableToolbarStyles from "@components/TableToolbar/tableToolbarStyles";

import { PopupActions } from "@store/popupStore";

import theme from "@theme/index";

interface ITableToolbarProperties extends PropsWithChildren {
  sx?: SxProps<Theme>;
}

/**
 * TableToolbar component
 *
 * A container component for organizing table toolbar elements.
 * It provides a consistent layout and styling for the toolbar contents.
 *
 * @param children - The child elements to be rendered within the toolbar.
 *
 * @returns A Box component containing the toolbar children.
 *
 * @example
 * ```tsx
 * <TableToolbar>
 *   <TableToolbarLeft>
 *     {* Left-aligned toolbar items *}
 *   </TableToolbarLeft>
 *   <TableToolbarRight>
 *     {* Right-aligned toolbar items *}
 *   </TableToolbarRight>
 * </TableToolbar>
 * ```
 */
export const TableToolbar = ({ children, sx }: ITableToolbarProperties) => {
  return (
    <Box
      sx={{
        ...tableToolbarStyles,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

/**
 * TableToolbarLeft component
 *
 * A container component for organizing left-aligned elements in a table toolbar.
 * It provides consistent layout and styling for the left side of the toolbar.
 *
 * @param children - The child elements to be rendered within the left side of the toolbar.
 *
 * @returns A Box component containing the left-aligned toolbar children.
 *
 * @example
 * ```tsx
 * <TableToolbarLeft>
 *   <TableToolbarTitle />
 * </TableToolbarLeft>
 * ```
 */
export const TableToolbarLeft = ({ children }: PropsWithChildren) => {
  return (
    <Box
      sx={{
        alignItems: "center",
        display: "flex",
        flexDirection: "row",
        gap: 1,
      }}
    >
      {children}
    </Box>
  );
};

/**
 * TableToolbarRight component
 *
 * A container component for organizing right-aligned elements in a table toolbar.
 * It provides consistent layout and styling for the right side of the toolbar.
 *
 * @param children - The child elements to be rendered within the right side of the toolbar.
 *
 * @returns A Box component containing the right-aligned toolbar children.
 *
 * @example
 * ```tsx
 * <TableToolbarRight>
 *   <TableToolbarExportDropdown />
 * </TableToolbarRight>
 * ```
 */
export const TableToolbarRight = ({ children }: PropsWithChildren) => {
  return <Box sx={{ alignItems: "center", display: "flex", flexDirection: "row", gap: 1 }}>{children}</Box>;
};

interface ITableToolbarTitleProperties {
  resourceLink?: string;
  tableSubtitle?: ReactNode;
  tableTitle: string;
  workflow?: string;
}

/**
 * TableToolbarTitle component
 *
 * A component for displaying the title of a table toolbar.
 * It provides a consistent layout and styling for the title of the toolbar.
 *
 * @param resourceLink - The link to the resource.
 * @param tableSubtitle - The subtitle of the table.
 * @param tableTitle - The title of the table.
 * @param workflow - The workflow of the table.
 *
 * @returns A Box component containing the title of the toolbar.
 *
 * @example
 * ```tsx
 * <TableToolbarTitle resourceLink="https://www.google.com" tableTitle="Index" workflow="companies" subtitle="Search by name or filter by industry, location, and more." />
 * ```
 */
export const TableToolbarTitle = ({
  resourceLink = "",
  tableSubtitle = "",
  tableTitle,
  workflow = "",
}: ITableToolbarTitleProperties) => {
  return (
    <Box>
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          flexDirection: "row",
          gap: 1,
        }}
      >
        <Typography variant="h5">{tableTitle}</Typography>
        {resourceLink && <ResourceLink resourceLink={resourceLink} workflow={workflow} />}
      </Box>
      <Typography sx={{ mt: 0.5 }} variant="body2">
        {tableSubtitle}
      </Typography>
    </Box>
  );
};

interface ITableToolbarSearchProperties {
  placeholder: string;
  query: string;
  setQuery: (query: string) => void;
}

/**
 * TableToolbarSearch component
 *
 * A component for providing a search input field in a table toolbar.
 * It allows users to search for specific items in the table.
 *
 * @param placeholder -The short hint displayed in the input before the user enters a value.
 * @param query - The current search query.
 * @param setQuery - A function to set the search query.
 *
 * @returns A Formik form with a TextField for search input.
 *
 * @example
 * ```tsx
 * <TableToolbarSearch query="search" setQuery={(query) => setSearchQuery(query)} />
 * ```
 */
export const TableToolbarSearch = ({ placeholder, query, setQuery }: ITableToolbarSearchProperties) => {
  const timeout = useRef<ReturnType<typeof setTimeout>>();

  const debouncedSetQuery = (newQuery: string) => {
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
    timeout.current = setTimeout(() => setQuery(newQuery), 750);
  };
  return (
    <Formik
      enableReinitialize
      initialValues={{
        query,
      }}
      onSubmit={(values) => {
        clearTimeout(timeout.current);
        setQuery(values.query);
      }}
    >
      {({ handleChange, setFieldValue, values }) => (
        <Form>
          <TextField
            color="secondary"
            InputProps={{
              endAdornment: values.query && (
                <InputAdornment position="end">
                  <IconButton
                    color="secondary"
                    edge="end"
                    onClick={() => {
                      void setFieldValue("query", "");
                      setQuery("");
                    }}
                  >
                    <ClearRoundedIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            label=""
            name="query"
            onChange={(event) => {
              handleChange(event);
              void setFieldValue("query", event.target.value);
              debouncedSetQuery(event.target.value);
            }}
            placeholder={placeholder}
            size="small"
            sx={{
              "& .MuiInputBase-root": {
                backgroundColor: theme.palette.common.white,
                borderRadius: "8px",
                height: "40px",
              },
              width: "256px",
            }}
            type="text"
            value={values.query}
            variant="outlined"
          />
        </Form>
      )}
    </Formik>
  );
};

interface ITableToolbarFilterProperties {
  activeFilters: number;
  toggleFilterVisibility: () => void;
  tooltipText: string;
}

/**
 * TableToolbarFilterButton component
 *
 * A component for displaying a button to toggle the visibility of filters in a table toolbar.
 * It provides a consistent layout and styling for the filter button.
 *
 * @param filters - The current filters applied to the table.
 * @param toggleFilterVisibility - A function to toggle the visibility of the filters.
 *
 * @returns A Button component with an icon and a tooltip.
 *
 * @example
 * ```tsx
 * <TableToolbarFilterButton filters={filters} toggleFilterVisibility={() => setShowFilters(!showFilters)} />
 * ```
 */
export const TableToolbarFilterButton = ({
  activeFilters,
  toggleFilterVisibility,
  tooltipText,
}: ITableToolbarFilterProperties) => {
  return (
    <Tooltip placement="bottom" title={tooltipText}>
      <Badge
        badgeContent={activeFilters}
        color="secondary"
        showZero={false}
        sx={{
          "& .MuiBadge-badge": {
            fontSize: "14px",
            transform: "translateX(5px) translateY(-8px)",
          },
        }}
      >
        <Button
          className="filter-controls"
          color={activeFilters > 0 ? "primary" : "secondary"}
          onClick={toggleFilterVisibility}
          startIcon={<FilterListRoundedIcon />}
          sx={{ borderRadius: "8px" }}
          variant="contained"
        >
          Filters
        </Button>
      </Badge>
    </Tooltip>
  );
};

interface ITableToolbarExportDropdownProperties extends PropsWithChildren {
  actionsLabel?: string;
  selectedIds: number[] | string[];
  tooltipText: string;
}

/**
 * TableToolbarExportDropdown component
 *
 * A component for displaying a dropdown menu for exporting company data in a table toolbar.
 * It provides a consistent layout and styling for the export dropdown menu.
 *
 * @param actionsLabel - The label for the export dropdown menu.
 * @param selectedIds - The IDs of the selected companies.
 * @param children - The children components to be rendered within the dropdown menu.
 * @param tooltipText - The tooltip text for the dropdown menu.
 *
 * @returns A ExportDropDown component with menu items for exporting company data.
 *
 * @example
 * ```tsx
 * <TableToolbarExportDropdown
 *   actionsLabel="Save Company"
 *   allCompanies={allCompanies}
 *   handleAddCompaniesToTechnology={handleAddCompaniesToTechnology}
 *   handleRemoveCompanies={handleRemoveCompanies}
 *   selectedIds={selectedIds}
 * />
 * ```
 */
export const TableToolbarDropdown = ({
  actionsLabel = "Save Company",
  children,
  selectedIds,
  tooltipText,
}: ITableToolbarExportDropdownProperties) => {
  return (
    <ExportDropDown canExport={selectedIds.length > 0} label={actionsLabel} tooltip={tooltipText}>
      {children}
    </ExportDropDown>
  );
};

interface ITableToolbarSelectProperties {
  hasToHaveOneChecked?: boolean;
  onChange: (value: string[]) => void;
  options: string[];
  value: string[];
}

export const TableToolbarSelect = ({
  hasToHaveOneChecked,
  onChange,
  options,
  value,
}: ITableToolbarSelectProperties) => {
  const handleChange = (newValues: string | string[]) => {
    const result = typeof newValues === "string" ? newValues.split(",") : newValues;
    if (result.length === 0 && hasToHaveOneChecked) {
      PopupActions.openAlert({
        duration: 4000,
        message: "At least one item must be selected.",
        severity: "error",
      });
      return;
    }
    onChange(result);
  };

  const resetSelection = () => {
    onChange(options);
  };

  return (
    <FormControl
      fullWidth
      margin="dense"
      size="small"
      sx={{
        m: 0,
      }}
    >
      <Select
        input={
          <OutlinedInput
            endAdornment={
              value.length < 2 && (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    onClick={resetSelection}
                    size="small"
                    style={{ marginRight: theme.spacing(1) }}
                    title="Reset selections"
                  >
                    <RestartAltRoundedIcon />
                  </IconButton>
                </InputAdornment>
              )
            }
          />
        }
        multiple
        onChange={(event) => handleChange(event.target.value)}
        renderValue={(selected) => selected.join(", ")}
        sx={{ backgroundColor: theme.palette.common.white, borderRadius: "8px" }}
        value={value}
      >
        {options.map((type) => (
          <MenuItem key={type} value={type}>
            <Checkbox checked={value.includes(type)} />
            <ListItemText primary={type} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export { default as TableToolbarDropdownMenuItem } from "@mui/material/MenuItem";
